import React, { useEffect, useState } from 'react';

import { Modal } from '@/components';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../api/warehouse.api';
import {
  type CapacityDraft,
  type CapacityErrors,
  capacityDraftOf,
  EMPTY_CAPACITY,
  parseCapacity,
  pickCapacity,
} from '../capacity';
import { norm } from '../format';
import { type FieldTarget, useServerErrors } from '../hooks/useServerErrors';
import { buildHubSnapshot } from '../snapshot';
import { STORAGE_CATEGORY_LABEL, STORAGE_TYPE_LABEL } from '../status';
import {
  type FieldError,
  type StorageCategory,
  type StorageStatus,
  type StorageType,
  type UpdateStoragePayload,
  type WarehouseStorage,
} from '../types';
import { useWarehouse } from '../WarehouseContext';
import { CapacityFields } from './CapacityFields';
import { EnumSelect, FormField, TextInput } from './form';
import { HubSelect } from './pickers';
import { StorageSelect } from './StorageSelect';

interface Props {
  open: boolean;
  initialHubID: number | null;
  /** Edit this storage (name, a location's type, status) instead of creating. */
  storage?: WarehouseStorage | null;
  onClose: () => void;
  onCreated: (storage: WarehouseStorage) => void;
  onUpdated?: (storage: WarehouseStorage) => void;
}

const PARENT_OF: Record<StorageCategory, StorageCategory | null> = {
  LOCATION: null,
  SECTION: 'LOCATION',
  BIN: 'SECTION',
};

type StorageFieldKey =
  'hub' | 'category' | 'parent' | 'type' | 'name' | 'status' | 'form';

/** Backend field (`hubID`, `parentID`, `name`, …) → storage form field. */
export const storageFieldOf = (e: FieldError): FieldTarget<StorageFieldKey> => {
  const n = norm(e.field || '');
  if (n.startsWith('hub')) return { key: 'hub' };
  if (n.startsWith('parent')) return { key: 'parent' };
  if (n === 'category' || n === 'type' || n === 'name' || n === 'status')
    return { key: n };
  return { key: 'form' };
};
const oneStep = () => 1;

const CATEGORY_OPTIONS = [
  { value: 'LOCATION', label: 'Location' },
  { value: 'SECTION', label: 'Section' },
  { value: 'BIN', label: 'Bin' },
];
const TYPE_OPTIONS = Object.entries(STORAGE_TYPE_LABEL).map(
  ([value, label]) => ({ value, label })
);
const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

/**
 * Tambah storage — Location → Section → Bin, name unique among its siblings.
 * A section picks its Location; a bin picks a Location first, then one of that
 * location's Sections (the Section picker waits for a Location). Both parents
 * are server-searched pickers, never the whole hub.
 *
 * With `storage` it edits instead: the name, a location's type and the
 * status (PATCH). Category and parent are fixed — moving a storage would
 * move its stock — so the path is shown, not picked.
 */
const StorageFormModal: React.FC<Props> = ({
  open,
  initialHubID,
  storage = null,
  onClose,
  onCreated,
  onUpdated,
}) => {
  const { hubs } = useWarehouse();
  const editing = !!storage;
  const [hubID, setHubID] = useState<number | null>(initialHubID);
  const [category, setCategory] = useState<StorageCategory>('BIN');
  const [locationID, setLocationID] = useState('');
  const [sectionID, setSectionID] = useState('');
  const [type, setType] = useState<StorageType>('STORAGE');
  const [status, setStatus] = useState<StorageStatus>('ACTIVE');
  const [name, setName] = useState('');
  const [cap, setCap] = useState<CapacityDraft>(EMPTY_CAPACITY);
  const [capErrors, setCapErrors] = useState<CapacityErrors>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 422 VALIDATION / a taken name → inline at the field.
  const server = useServerErrors(storageFieldOf, oneStep);
  const { setErrors, fieldError: fe } = server;
  const formErrors = server.errors
    .filter((e) => storageFieldOf(e).key === 'form')
    .map((e) => e.message);
  const alert = error ?? (formErrors.length ? formErrors.join(' ') : null);

  useEffect(() => {
    if (!open) return;
    setHubID(storage?.hubID ?? initialHubID);
    setCategory(storage?.category ?? 'BIN');
    setLocationID('');
    setSectionID('');
    setType(storage?.type ?? 'STORAGE');
    setStatus(storage?.status ?? 'ACTIVE');
    setName(storage?.name ?? '');
    setCap(capacityDraftOf(storage));
    setCapErrors({});
    setError(null);
    setErrors([]);
    setBusy(false);
  }, [open, initialHubID, storage, setErrors]);

  const parentCat = PARENT_OF[category];
  const parentID = category === 'BIN' ? sectionID : locationID;
  const nameLabel = `${STORAGE_CATEGORY_LABEL[category]} Name`;

  if (!open) return null;

  const nameProblem = !name.trim() ? `${nameLabel} wajib diisi.` : null;

  const update = async (current: WarehouseStorage) => {
    if (nameProblem) {
      setErrors([]);
      setError(nameProblem);
      return;
    }
    // Capacity is a BIN's: a changed field goes out, null clears it.
    const capacity = parseCapacity(cap);
    if (current.category === 'BIN' && Object.keys(capacity.errors).length) {
      setErrors([]);
      setCapErrors(capacity.errors);
      setError('Periksa isian dimensi dan kapasitas.');
      return;
    }
    const capPatch: UpdateStoragePayload =
      current.category === 'BIN'
        ? pickCapacity(
            capacity.values,
            (k) => capacity.values[k] !== (current[k] ?? null)
          )
        : {};
    const patch: UpdateStoragePayload = {
      ...(name.trim() !== current.name ? { name: name.trim() } : {}),
      ...(current.category === 'LOCATION' && type !== current.type
        ? { type }
        : {}),
      ...(status !== current.status ? { status } : {}),
      ...capPatch,
    };
    if (!Object.keys(patch).length) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    setErrors([]);
    try {
      const updated = await warehouseApi.updateStorage(current.id, patch);
      onUpdated?.(updated);
    } catch (err) {
      const shown = server.fromError(err, {
        STORAGE_NAME_TAKEN: 'name',
        INVALID_TYPE: 'type',
        PARENT_INACTIVE: 'status',
      });
      setError(shown.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (storage) return update(storage);
    const missing: string[] = [];
    if (!hubID) missing.push('Hub wajib dipilih.');
    if (parentCat && !locationID) missing.push('Location wajib dipilih.');
    if (category === 'BIN' && locationID && !sectionID)
      missing.push('Section wajib dipilih.');
    if (nameProblem) missing.push(nameProblem);
    const capacity = parseCapacity(cap);
    const capInvalid =
      category === 'BIN' && Object.keys(capacity.errors).length > 0;
    if (capInvalid) missing.push('Periksa isian dimensi dan kapasitas.');
    if (missing.length || !hubID) {
      setErrors([]);
      setCapErrors(capInvalid ? capacity.errors : {});
      setError(missing.join(' '));
      return;
    }
    setCapErrors({});
    const snap = buildHubSnapshot(hubs, hubID);
    setBusy(true);
    setError(null);
    setErrors([]);
    try {
      const created = await warehouseApi.createStorage({
        hubID: snap.hubID,
        hubName: snap.hubName,
        hubCode: snap.hubCode,
        name: name.trim(),
        category,
        ...(category === 'LOCATION' ? { type } : {}),
        ...(parentCat ? { parentID } : {}),
      });
      // The create call carries no capacity: a bin's dimensions / capacity
      // follow in one PATCH (only the fields that were filled).
      const capacityBody: UpdateStoragePayload =
        category === 'BIN'
          ? pickCapacity(capacity.values, (k) => capacity.values[k] !== null)
          : {};
      if (!Object.keys(capacityBody).length) {
        onCreated(created);
        return;
      }
      try {
        onCreated(await warehouseApi.updateStorage(created.id, capacityBody));
      } catch (err) {
        onCreated(created);
        toast.error('Storage dibuat, tetapi kapasitas belum tersimpan', {
          description: `${parseWarehouseError(err).message} Atur lewat Edit.`,
        });
      }
    } catch (err) {
      const shown = server.fromError(err, {
        STORAGE_NAME_TAKEN: 'name',
        // A location's parent problem is its missing type.
        INVALID_PARENT: parentCat ? 'parentID' : 'type',
      });
      setError(shown.message);
    } finally {
      setBusy(false);
    }
  };

  /** Where the edited storage sits (its parents can't change here). */
  const parentPath = storage
    ? [storage.location, storage.section]
        .filter((x) => x && x.id !== storage.id)
        .map((x) => x?.name)
        .join(' / ')
    : '';

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      icon={editing ? 'ri-pencil-line' : 'ri-stack-line'}
      title={
        editing ? `Edit ${STORAGE_CATEGORY_LABEL[category]}` : 'Tambah storage'
      }
      subtitle={
        editing
          ? [parentPath, storage?.name].filter(Boolean).join(' / ')
          : undefined
      }
      width="lg"
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={busy}
          >
            Batal
          </Button>
          <Button onClick={submit} loading={busy}>
            {editing ? 'Simpan perubahan' : 'Simpan storage'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {alert && (
          <Alert
            status="error"
            appearance="stroke"
            size="sm"
            data-testid="storage-error"
          >
            {alert}
          </Alert>
        )}
        {editing ? (
          <div
            className="rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2.5 text-[13px]"
            data-testid="st-edit-path"
          >
            <p className="label-eyebrow">
              {STORAGE_CATEGORY_LABEL[category]} ·{' '}
              {storage?.hub?.name ?? `Hub #${storage?.hubID}`}
            </p>
            <p className="mt-1 font-medium text-text-strong-950">
              {parentPath ? `${parentPath} / ` : ''}
              {storage?.name}
            </p>
          </div>
        ) : null}
        {!editing && (
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Hub" required error={fe('hub')}>
              <HubSelect
                value={hubID}
                onChange={(id) => {
                  setHubID(id);
                  setLocationID('');
                  setSectionID('');
                }}
                invalid={!!fe('hub')}
              />
            </FormField>
            <FormField
              label="Category"
              htmlFor="st-cat"
              required
              error={fe('category')}
            >
              <EnumSelect
                id="st-cat"
                invalid={!!fe('category')}
                value={category}
                onChange={(v) => {
                  setCategory(v as StorageCategory);
                  setLocationID('');
                  setSectionID('');
                }}
                options={CATEGORY_OPTIONS}
              />
            </FormField>
          </div>
        )}
        {editing && category !== 'LOCATION' ? null : parentCat ? (
          <>
            <FormField
              label="Location"
              required
              error={category === 'SECTION' ? fe('parent') : null}
              testId="st-location"
            >
              <StorageSelect
                hubID={hubID}
                category="LOCATION"
                value={locationID}
                placeholder="Pilih location"
                error={category === 'SECTION' && !!fe('parent')}
                onChange={(id) => {
                  if (id === locationID) return;
                  setLocationID(id);
                  setSectionID('');
                }}
              />
            </FormField>
            {category === 'BIN' && (
              <FormField
                label="Section"
                required
                error={fe('parent')}
                testId="st-section"
              >
                <StorageSelect
                  hubID={hubID}
                  category="SECTION"
                  parentID={locationID}
                  value={sectionID}
                  disabled={!locationID}
                  placeholder={
                    locationID ? 'Pilih section' : 'Pilih location dulu'
                  }
                  error={!!fe('parent')}
                  onChange={setSectionID}
                />
              </FormField>
            )}
          </>
        ) : (
          <FormField label="Type" htmlFor="st-type" error={fe('type')}>
            <EnumSelect
              id="st-type"
              invalid={!!fe('type')}
              value={type}
              onChange={(v) => setType(v as StorageType)}
              options={TYPE_OPTIONS}
            />
          </FormField>
        )}
        <FormField
          label={nameLabel}
          htmlFor="st-name"
          required
          error={fe('name')}
        >
          <TextInput
            id="st-name"
            size="md"
            invalid={!!fe('name')}
            value={name}
            onChange={setName}
            placeholder={category === 'BIN' ? 'Slot A4' : 'Rak C'}
          />
        </FormField>
        {category === 'BIN' && (
          <section
            className="flex flex-col gap-3 border-t border-stroke-soft-200 pt-4"
            data-testid="st-capacity-section"
          >
            <div>
              <p className="text-[13px] font-semibold tracking-tight text-text-strong-950">
                Dimensi dan kapasitas
              </p>
              <p className="mt-0.5 text-[11px] text-text-sub-600">
                Kosong = belum diset. Isi dimensi dan m³ terhitung otomatis;
                ubah m³ langsung bila perlu.
              </p>
            </div>
            <CapacityFields
              idPrefix="st"
              value={cap}
              onChange={setCap}
              errors={capErrors}
              disabled={busy}
            />
          </section>
        )}
        {editing && (
          <FormField label="Status" htmlFor="st-status" error={fe('status')}>
            <EnumSelect
              id="st-status"
              invalid={!!fe('status')}
              value={status}
              onChange={(v) => setStatus(v as StorageStatus)}
              options={STATUS_OPTIONS}
            />
          </FormField>
        )}
      </div>
    </Modal>
  );
};

export default StorageFormModal;
